import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Link
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useTenant } from '../contexts/TenantContext';

const PhoneContactDialog = ({ open, onClose, action = 'reschedule' }) => {
  const { tenant } = useTenant();
  
  const actionConfig = {
    reschedule: {
      title: 'Reschedule Your Booking',
      icon: <ScheduleIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />,
      message: 'To reschedule your booking, please call us directly. Our team will help you find a new date and time that works best for you.',
      color: 'primary'
    },
    cancel: {
      title: 'Cancel Your Booking',
      icon: <CancelIcon color="warning" sx={{ fontSize: 48, mb: 2 }} />,
      message: 'To cancel your booking, please call us directly. We\'ll be happy to assist you with the cancellation process.',
      color: 'warning'
    }
  };

  const config = actionConfig[action] || actionConfig.reschedule;
  const phoneNumber = tenant?.clientSupportPhone || tenant?.contactPhone || '';
  
  // Format phone number for display (e.g., (555) 123-4567)
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  // Format phone number for tel: link (e.g., +15551234567)
  const formatPhoneLink = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10 ? `+1${cleaned}` : phone;
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          textAlign: 'center'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {config.title}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {config.icon}
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            {config.message}
          </Typography>

          {phoneNumber && (
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <PhoneIcon color={config.color} />
              <Link 
                href={`tel:${formatPhoneLink(phoneNumber)}`}
                color={config.color}
                underline="none"
                sx={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 'bold',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {formatPhoneDisplay(phoneNumber)}
              </Link>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Our support team is available to assist you with your booking needs.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ minWidth: 100 }}
        >
          Close
        </Button>
        {phoneNumber && (
          <Button 
            component={Link}
            href={`tel:${formatPhoneLink(phoneNumber)}`}
            variant="contained"
            color={config.color}
            startIcon={<PhoneIcon />}
            sx={{ minWidth: 140 }}
          >
            Call Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PhoneContactDialog;